import { Component, OnInit } from '@angular/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ApiService } from 'src/app/api.service';
import { ActivatedRoute, NavigationExtras, Router } from '@angular/router';
import { AuthenticationService } from 'src/app/_services';
import { SecurityDocument } from 'src/app/shared/models/SecurityDocument';
import { first } from 'rxjs/operators';
import { Constants } from 'src/app/shared/vo/Constants';
import { TovpilLanguageService } from 'src/app/utils/tovpil-language.service';
import { CountryVO } from 'src/app/shared/models/CountryVO';

@Component({
  selector: 'app-price-country',
  templateUrl: './price-country.component.html',
  styleUrls: ['./price-country.component.scss'],
  providers: [MessageService, ConfirmationService, TovpilLanguageService]
})
export class PriceCountryComponent implements OnInit {

  /** SECURITY */
  currentUser: any;
  security: SecurityDocument;
  token: string;
  device: string;
  storeId: string;

  /** LABELS */
  translations: Record<string, string> = {};
  failedMsg : string;

  /** COMPONENT */
  stores: CountryVO[];

  /** COMPONENT EXPORT */
  blocked: boolean = true;

  /**
   * The constructor initializes various services and variables, and checks for authentication based on
   * the provided token and device parameters.
   * @param {MessageService} messageService - The `messageService` is a service that handles displaying
   * messages to the user. It is used to show notifications, error messages, success messages, etc.
   * @param {ApiService} apiService - The `apiService` parameter is an instance of the `ApiService`
   * class, which is responsible for making API requests and handling responses. It likely contains
   * methods for performing various API operations, such as retrieving data from a server or sending data
   * to a server.
   * @param {ActivatedRoute} route - The `route` parameter is an instance of the `ActivatedRoute` class,
   * which is used to access the current route's information. It provides access to the route's
   * parameters, query parameters, and other related data. In the code snippet, it is used to subscribe
   * to the `queryParams` observable
   * @param {AuthenticationService} authenticationService - The `authenticationService` parameter is a
   * service that handles user authentication and authorization. It is used to authenticate the user and
   * retrieve the current user's information.
   * @param {TovpilLanguageService} translationService - The `translationService` parameter is an
   * instance of the `TovpilLanguageService` class. It is used for handling translations in the
   * application.
   * @returns The constructor does not return anything. It is used to initialize the properties of the
   * class and perform any necessary setup tasks.
   */
  constructor(
    private messageService: MessageService,
    private apiService: ApiService,
    private route: ActivatedRoute,
    private router: Router,
    private authenticationService: AuthenticationService,
    private translationService: TovpilLanguageService
  ) {
    // ROUTER PARAMS
    this.route.queryParams.subscribe(params => {
      this.token = params.token;
      this.device = params.device;
      this.storeId = params.storeId;
    });

    // INIT AUTH
    if (!this.token || this.device !== Constants.APP_USA || this.device !== Constants.APP_DEVICE_ID) {
      this.currentUser = this.authenticationService.currentUserValue;
      return;
    }
    this.authenticateByZuul('generalaccess', 'hY6Mn_4hn9vDrWrh6');
  }

  /**
   * The ngOnInit function initializes the component by setting the currentUser property to the current
   * user value from the authentication service and calling the getSecurity function.
   */
  ngOnInit(): void {
    this.currentUser = this.authenticationService.currentUserValue;
    this.getSecurity();
  }

  /**
   * The function `authenticateByZuul` authenticates a user by calling the `login` method of the
   * `authenticationService` and performs certain actions based on the response data.
   * @param {string} user - A string representing the username or user identifier.
   * @param {string} password - The password parameter is a string that represents the user's password.
   */
  authenticateByZuul(user: string, password: string) {
    this.authenticationService.login(user, password)
      .pipe(first())
      .subscribe(
        data => {
          if (data.code === 'R-0200') {
            this.getSecurity();
          } else {
            this.messageService.add({ severity: 'warn', summary: 'Authentication', detail: `Error: ${this.translations.lbl_login}, ${this.translations.lbl_then_try_it_again}`});
          }
        },
        error => {
          this.messageService.add({ severity: 'warn', summary: 'Authentication', detail: `Error: ${this.translations.lbl_login}, ${this.translations.lbl_then_try_it_again}`});
        }
      );
  }

  /**
   * The function `getSecurity()` retrieves security data from an API service and then loads the
   * translated labels.
   */
  getSecurity() {
    if (!this.security) {
      this.apiService.findSecurityById({ "id": this.token ?? this.currentUser?.tokenProfile }).then(
        data => {
          this.security = data;
          this.load();
        }
      );
    } else {
      this.getTranslatedLabels();
    }
  }

  /**
   * The goBack function closes the current window.
   */
  goBack() {
    window.close();
  }

  /**
   * The function retrieves translated labels using the language and region specified in the security
   * object.
   */
  getTranslatedLabels() {
    this.translationService.getTranslatedLabels(this.security.language, this.security.region).then(() => {
      this.translations = this.translationService.getLabels();
      this.failedMsg = this.translations.lbl_internal_server_error;
    });
  }

  /**
   * The `load` function is responsible for loading data and translated labels.
   */
  private load() {
    console.debug("Loading ...")
    this.getTranslatedLabels();
    this.getData();
  }

  /**
   * The `getData()` function fetches a list of dependent local stores from an API service, displays a
   * success message if the data is retrieved successfully, displays a warning message if there is an
   * error, and sets the `blocked` property to false after a delay of 1500 milliseconds.
   */
  getData() {
    this.blocked = true;
    this.apiService.getCoutriesFromStore({
      storeId: this.security.storeId,
      language: this.security.language
    }).then(
      data => {
        this.stores = data;
        // this.messageService.add({ severity: 'success', summary: 'Lista de Ciudad', detail: Constants.SUCCESS_MESSAGE });
      }).catch(error => {
        console.error("An error occurred while fetching purchase order data:");
        this.messageService.add({ severity: 'warn', summary: `${this.translations.lbl_list} ${this.translations.lbl_of} ${this.translations.lbl_city}`, detail: Constants.FAILED_MESSAGE });
      }).finally(() => {
        setTimeout(() => {
          this.blocked = false;
        }, 1500);
      });
  }

  /**
   * The function redirects the user to the price list page with the security information passed as a
   * parameter.
   */
  redirectToPriceListByCountry(item: CountryVO) {
    try {
      let navigationExtras: NavigationExtras = {
        state: JSON.parse(JSON.stringify({
          security: this.security,
          country: item
        }))
      };

      try {
        this.router.navigate(['/treasurer/priceList'], navigationExtras);
      } catch (navigationError) {
        console.error("An error occurred while navigating to coordination catalog details:", navigationError);
        this.messageService.add({ severity: 'warn', summary: `${this.translations.lbl_list} ${this.translations.lbl_of} ${this.translations.lbl_city}`, detail: `Error: ${navigationError}`, life: 5000 });
      }
    } catch (parseError) {
      console.error("An error occurred while parsing state to JSON:", parseError);
      this.messageService.add({ severity: 'warn', summary: `${this.translations.lbl_list} ${this.translations.lbl_of} ${this.translations.lbl_city}`,  detail: `Error: ${parseError}`, life: 5000 });
    }
  }
}
